import OtherPropertyExistingMortgage from "./other-property-existing-mortgage.vue";
import SectionTitle from "@Client/@Modules/application-standalone/components/helpers/section-title.vue";
import PageSectionGroupLayout from "@Client/@Modules/application-standalone/components/layout/page-section-group-layout.vue";
import { defineComponent, toRefs, watch, ref, computed, onMounted } from "@vue/composition-api";
import CurrencyField from "@Shared/components/form-elements/fields/currency-field.vue";
import PercentageField from "@Shared/components/form-elements/fields/percentage-field.vue";
import SelectField from "@Shared/components/form-elements/fields/select-field.vue";
import AddressFieldGroup from "@Shared/components/form-elements/field-groups/address-field-group.vue";
import { useApplicantHelpers } from "@Client/@Composables/application-standalone/pages/applicants/useApplicantHelpers";
import { useOtherPropertiesHelpers } from "@Client/@Composables/application-standalone/pages/other-properties/useOtherPropertiesHelpers";
import { APPLICATION_FIELD_OPTIONS } from "@Shared/@Utils/application-defaults-utils/global-field-options";
import RadioField from "@Shared/components/form-elements/fields/radio-field.vue";
import DateField from "@Shared/components/form-elements/fields/date-field.vue";
import { delay } from "@Shared/@Utils/utils";
import { useAddress } from "@Shared/@Composables/generic/useAddress";
import { useApplicantFieldBindings } from "@Client/@Composables/application-standalone/pages/applicants/useApplicantFieldBindings";
import { otherPropertyValidator } from "@Client/@Composables/application-standalone/validations/otherPropertyValidator";
import { otherPropertiesBindingsAndVisibilities } from "@Client/@Composables/application-standalone/pages/other-properties/otherPropertiesBindingsAndVisibilities";
import { get } from "lodash";
export default defineComponent({
  name: "OtherPropertyInformation",
  components: {
    OtherPropertyExistingMortgage,
    PageSectionGroupLayout,
    SectionTitle,
    CurrencyField,
    SelectField,
    PercentageField,
    AddressFieldGroup,
    RadioField,
    DateField
  },
  props: {
    applicantIndex: {
      type: Number,
      required: true
    },
    otherPropertyIndex: {
      type: Number,
      required: true
    },
    applicantId: {
      type: String,
      required: true
    },
    showErrors: {
      type: Number,
      default: 0
    }
  },
  setup(props, {
    emit
  }) {
    const {
      applicantIndex,
      otherPropertyIndex,
      applicantId,
      showErrors
    } = toRefs(props);
    const addressFieldComponent = ref();
    const extraValidation = ref(true);
    const {
      applicantNameFromIndex,
      applicantsAsOptions,
      allApplicants,
      addressesAsOptions
    } = useApplicantHelpers();
    const isPropertyStillMortgaged = ref(false);
    const showCopiedIndicator = ref(false);
    const applicantName = applicantNameFromIndex(applicantIndex.value);
    const {
      propertyDetailsOccupancyOptions,
      propertyRentalIncomeSelectOptions,
      yesNoOptions
    } = APPLICATION_FIELD_OPTIONS();
    const {
      propertyHasExistingMortgages,
      addExistingMortgage,
      handleChangeIsThePropertyStillMortgaged,
      computedOtherPropertyHasExistingMortgages
    } = useOtherPropertiesHelpers({
      otherPropertyIndex: otherPropertyIndex.value,
      applicantIndex: applicantIndex.value
    });
    const {
      own
    } = useApplicantFieldBindings({
      applicantIndex
    });
    const {
      bindings,
      visibilities
    } = otherPropertiesBindingsAndVisibilities(applicantIndex, otherPropertyIndex, applicantId);
    const {
      purchasePrice,
      estimatedValue,
      purchaseDate,
      address,
      propertyOwner,
      feesIncludeHeat,
      heating,
      includeInTds,
      maintenanceFees,
      percOfTotalMaintFees,
      propertyTaxes,
      hydro,
      insurance,
      interest,
      management,
      monthlyIncome,
      occupancy,
      others,
      repairs,
      rentalIncomeOptions,
      rentalIncomeOptionsPercent,
      otherPropertyFromApplicant: otherProperty
    } = bindings;
    onMounted(() => {
      includeInTds.value = true;
    });
    const handleOwnerChange = newApplicantIndex => {
      var _allApplicants$value$;
      if (typeof newApplicantIndex !== "number") return;
      propertyOwner.value = newApplicantIndex;
      const properties = (_allApplicants$value$ = allApplicants.value[newApplicantIndex]) == null ? void 0 : _allApplicants$value$["other-properties"];
      const applicant = allApplicants.value[newApplicantIndex];
      if (properties) {
        const newOtherPropertyIndex = properties.length - 1;
        const newApplicantId = applicant.id;
        emit("navigate", {
          applicantIndex: newApplicantIndex,
          applicantId: newApplicantId,
          otherPropertyIndex: newOtherPropertyIndex
        });
      }
    };
    const handleAddressCopy = async newAdress => {
      if (newAdress) {
        showCopiedIndicator.value = true;
        address.value.line1 = newAdress.line1 || "";
        address.value.line2 = newAdress.line2 || "";
        address.value["unit-number"] = newAdress["unit-number"] || "";
        address.value.province = newAdress.province || "";
        address.value.city = newAdress.city || "";
        address.value["postal-code"] = newAdress["postal-code"] || "";
        address.value["street-direction"] = newAdress["street-direction"] || "";
        address.value["street-name"] = newAdress["street-name"] || "";
        address.value["street-number"] = newAdress["street-number"] || "";
        address.value["street-type"] = newAdress["street-type"] || "";
        address.value["formatted-address"] = useAddress(newAdress).fullAddress.value;
        await delay(3000);
        showCopiedIndicator.value = false;
      }
    };
    const {
      otherPropertyPurchasePriceVisibility,
      otherPropertyEstimatedValueVisibility,
      otherPropertyPurchaseDateVisibility,
      otherPropertyAddressVisibility,
      otherPropertyFeesIncludeHeatVisibility,
      otherPropertyHeatingVisibility,
      otherPropertyIncludeInTdsVisibility,
      otherPropertyMaintenanceFeesVisibility,
      otherPropertyPercOfTotalMaintFeesVisibility,
      otherPropertyPropertyTaxesVisibility,
      otherPropertyHydroVisibility,
      otherPropertyInsuranceVisibility,
      otherPropertyInterestVisibility,
      otherPropertyManagementVisibility,
      otherPropertyMonthlyIncomeVisibility,
      otherPropertyOccupancyVisibility,
      otherPropertyOthersVisibility,
      otherPropertyRentalIncomeOptionsVisibility,
      otherPropertyRentalIncomeOptionsPercentVisibility,
      otherPropertyRepairsVisibility
    } = visibilities;
    const existingMortgages = computed(() => otherProperty.value["other-property-existing-mortgages"] || []);
    const showCopyPreviousAddress = computed(() => addressesAsOptions.value.length && own.value === "own");
    watch(isPropertyStillMortgaged, newValue => {
      if (newValue) addExistingMortgage(applicantIndex.value, otherPropertyIndex.value);
    });
    watch(existingMortgages, newValue => {
      if (newValue.length === 0) isPropertyStillMortgaged.value = false;
    });
    const {
      v$
    } = otherPropertyValidator(applicantIndex, otherPropertyIndex);
    const isValid = computed(() => {
      var _v$$value;
      return !(v$ != null && (_v$$value = v$.value) != null && _v$$value.$invalid);
    });
    watch(isValid, newVal => {
      emit("disable-save", !newVal);
    }, {
      immediate: true
    });
    watch(showErrors, newVal => {
      if (newVal) {
        var _v$$value2, _addressFieldComponen;
        v$ == null ? void 0 : (_v$$value2 = v$.value) == null ? void 0 : _v$$value2.$touch();
        (_addressFieldComponen = addressFieldComponent.value) == null ? void 0 : _addressFieldComponen.openManualAddress(true);
      }
    });
    const getValidator = path => get(v$.value, path, null);
    return {
      propertyHasExistingMortgages,
      otherProperty,
      applicantsAsOptions,
      applicantName,
      propertyOwner,
      purchasePrice,
      estimatedValue,
      purchaseDate,
      address,
      feesIncludeHeat,
      heating,
      includeInTds,
      maintenanceFees,
      percOfTotalMaintFees,
      propertyTaxes,
      hydro,
      insurance,
      interest,
      management,
      monthlyIncome,
      occupancy,
      others,
      rentalIncomeOptions,
      rentalIncomeOptionsPercent,
      repairs,
      propertyDetailsOccupancyOptions,
      propertyRentalIncomeSelectOptions,
      handleOwnerChange,
      addExistingMortgage,
      otherPropertyPurchasePriceVisibility,
      otherPropertyEstimatedValueVisibility,
      otherPropertyPurchaseDateVisibility,
      otherPropertyAddressVisibility,
      otherPropertyFeesIncludeHeatVisibility,
      otherPropertyHeatingVisibility,
      otherPropertyIncludeInTdsVisibility,
      otherPropertyMaintenanceFeesVisibility,
      otherPropertyPercOfTotalMaintFeesVisibility,
      otherPropertyPropertyTaxesVisibility,
      otherPropertyHydroVisibility,
      otherPropertyInsuranceVisibility,
      otherPropertyInterestVisibility,
      otherPropertyManagementVisibility,
      otherPropertyMonthlyIncomeVisibility,
      otherPropertyOccupancyVisibility,
      otherPropertyOthersVisibility,
      otherPropertyRentalIncomeOptionsVisibility,
      otherPropertyRentalIncomeOptionsPercentVisibility,
      otherPropertyRepairsVisibility,
      yesNoOptions,
      isPropertyStillMortgaged,
      existingMortgages,
      v$,
      addressesAsOptions,
      handleAddressCopy,
      handleChangeIsThePropertyStillMortgaged,
      computedOtherPropertyHasExistingMortgages,
      showCopiedIndicator,
      showCopyPreviousAddress,
      getValidator,
      extraValidation,
      addressFieldComponent
    };
  }
});